import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { LiveSummary, SourceWidget } from '../live-ui/LiveChrome'
import { SignalMap } from '../live-ui/SignalMap'
import { agoText, viewersText } from '../live-ui/PlatformBlock'
import { DestinationEditor } from '../live-ui/DestinationEditor'
import { srtCells } from '../live-ui/srt'
import { FillRow } from '../live-ui/FillRow'
import { STATE_SPEC } from '../live-ui/quality'
import { delaySec, serverName } from '../live-ui/format'
import { Banner, EditButton, LogoSlot, RelayToggle, Spark, StatusBadge } from '../live-ui/ui'
import { BitrateChart } from '../live-ui/BitrateChart'
import { Button, Icon, Note, Tag } from '../ui'
import type { LiveModel } from '../live-ui/adapter'
import { driftText, driftWarn, sourceWords, type PairView } from '../live-ui/vertical'
import { verticalVideo } from '../live-ui/studio/Pair'
import { videoLabel } from '../live-ui/setup'
import { YouTubeDualWizard } from '../live-ui/studio/YouTubeDual'
import type { Profile } from '../api'
import type { Destination } from '../live-ui/types'
import { t } from '../i18n'
import { isDropped, needsYou, rowTone } from '../live-ui/studio/model'
import { Soon } from '../live-ui/studio/Right'
import { StudioBar } from '../live-ui/studio/StudioBar'
import { ProtocolChip } from '../live-ui/ProtocolSettings'
import { AddForm, Catalog } from '../live-ui/studio/AddOutput'
import { useLiveScreen, useMaxOutputs, useMedia } from '../live-ui/studio/useLiveScreen'

/** Bitrate over 5 minutes, as a 90 × 18 line in the state's colour. */
const ISSUE = { lagging: 'LAGGING', slow_link: 'SLOW LINK', translation_lag: 'TRANSLATION LAG', translation_fallback: 'ORIGINAL AUDIO' } as const

function badgeOf(d: Destination, now: number): { text: string; color: 'live' | 'start' | 'warn' | 'fail' | 'idle'; border: 'solid' | 'dashed' | 'dotted' } {
  const spec = STATE_SPEC[d.state]
  if (d.issue) return { text: ISSUE[d.issue], color: 'warn', border: spec.border }
  if (d.needsKey) return { text: 'NEEDS KEY', color: 'warn', border: 'dotted' }
  const left = d.retryAt ? Math.max(0, Math.ceil((d.retryAt - now) / 1000)) : undefined
  if (d.state === 'reconnecting' && left) return { text: `RETRY ${left} S`, color: 'warn', border: spec.border }
  return { text: spec.badge, color: spec.color, border: spec.border }
}

/**
 * What the platform itself says, as one line under the output's name (the "On the platforms"
 * cards are gone, owner 18.10): the connect link, the platform's first issue, or that its API
 * is silent. The other numbers (likes, uptime, new followers) are in the Watching cell's title.
 */
function PlatformLine({ d, connecting, onConnect }: { d: Destination; connecting: boolean; onConnect: (p: 'youtube' | 'twitch') => void }) {
  const b = d.platformBlock
  if (!b) return null
  if (b.state === 'connect' && b.connect) {
    const p = b.connect
    return (
      <button type="button" data-testid={`status-connect-${d.id}`} disabled={connecting} onClick={() => onConnect(p)}
        className="mt-0.5 flex items-center gap-1 text-left text-xs font-medium text-accent underline-offset-2 hover:underline disabled:text-muted">
        {connecting ? `Opening ${b.label}…` : <>{`Connect ${b.label} account`}<Icon name="external" size={12} /></>}
      </button>
    )
  }
  const issue = (b.state === 'issue' || b.state === 'stale') ? b.issues[0] : undefined
  if (issue) {
    const st = issue.severity === 'error' ? 'fail' : 'warn'
    return <span role="status" data-testid={`status-issue-${d.id}`} className={`mt-0.5 flex items-center gap-1 text-xs ${st === 'fail' ? 'text-fail' : 'text-warn'}`}><Icon name={`state-${st}`} size={12} />{issue.text}</span>
  }
  if (b.state === 'stale') return <span data-testid={`status-stale-${d.id}`} className="mt-0.5 flex items-center gap-1 text-xs text-warn"><Icon name="state-warn" size={12} />{b.label} API not answering</span>
  return null
}

/** "842 watching · 1.1K likes · uptime 1 h 12 m · +18 new followers · 12 s ago" for the Watching cell. */
function platformTitle(d: Destination, now: number): string | undefined {
  const b = d.platformBlock
  const st = b?.stats
  if (!b || !st) return undefined
  const parts = [`${viewersText(st.viewers)} watching on ${b.label}`]
  if (b.cells.includes('likes')) parts.push(st.likes !== undefined ? `${st.likes} likes` : 'likes hidden')
  if (b.cells.includes('uptime') && st.uptimeS !== undefined) parts.push(`uptime ${Math.floor(st.uptimeS / 3600)} h ${Math.floor((st.uptimeS % 3600) / 60)} m`)
  if (b.cells.includes('followers') && st.newFollowers !== undefined) parts.push(`+${st.newFollowers} new followers`)
  if (b.updatedAt !== undefined) parts.push(`${b.state === 'stale' ? 'last known, ' : ''}${agoText(now - b.updatedAt)}`)
  return parts.join(' · ')
}

/**
 * Outputs: state, bitrate over 5 minutes, delay to viewers, watching, reconnects, and
 * the edit button; the output's settings open under its row (one at a time, as in the Studio).
 */
/**
 * VERT-1 (mockup E, VT-16): both inputs — video, Mbps, drift against main; over 1 s the vertical row and a note
 * say it in warn (the broadcast goes on).
 */
function InputsTable({ m, pair }: { m: LiveModel; pair: PairView }) {
  const s = m.summary
  const warn = driftWarn(pair.driftMs)
  return (
    <section data-testid="status-inputs" aria-label={t('vert.st.inputs')} className="sx-panel flex flex-col gap-2 !p-0">
      <div className="flex min-h-11 items-center border-b border-line px-3"><span className="sx-label">{t('vert.st.inputs')}</span></div>
      <table className="sx-table compact">
        <thead>
          <tr>
            <th scope="col">{t('vert.sum.input')}</th>
            <th scope="col" className="num">{t('vert.sum.video')}</th>
            <th scope="col" className="num">{t('status.col.bitrate')}</th>
            <th scope="col" className="num">{t('vert.sum.drift')}</th>
          </tr>
        </thead>
        <tbody>
          <tr data-testid="status-input-main">
            <td><span className="flex items-center gap-2"><Icon name={s.live ? 'state-live' : 'state-idle'} size={12} className={s.live ? 'text-live' : 'text-muted'} />{t('vert.row.main')}</span></td>
            <td className="num tabular">{s.live ? videoLabel(s.resolution, s.fps) ?? '—' : '—'}</td>
            <td className="num tabular">{s.bitrateMbps !== undefined ? s.bitrateMbps.toFixed(1) : '—'}</td>
            <td className="num tabular text-muted">—</td>
          </tr>
          <tr data-testid="status-input-vertical" data-state={warn ? 'warn' : pair.live ? 'live' : 'idle'}>
            <td><span className="flex items-center gap-2">
              <Icon name={warn ? 'state-warn' : pair.live ? 'state-live' : 'state-idle'} size={12} className={warn ? 'text-warn' : pair.live ? 'text-live' : 'text-muted'} />
              {t('vert.row.vertical')}<Tag>{t('vert.tag')}</Tag></span></td>
            <td className="num tabular">{verticalVideo(pair.resolution, pair.fps) ?? '—'}</td>
            <td className="num tabular">{pair.bitrateMbps !== undefined ? pair.bitrateMbps.toFixed(1) : '—'}</td>
            <td className={`num tabular ${warn ? 'warn' : ''}`}>{driftText(pair.driftMs)}</td>
          </tr>
        </tbody>
      </table>
      {warn && <div className="px-3 pb-3"><Note state="warn" data-testid="status-drift-note"><b className="font-medium">{t('vert.st.driftRow', { drift: driftText(pair.driftMs) })}.</b> {t('vert.st.driftHint')}</Note></div>}
    </section>
  )
}

function OutputsTable({ dests, now, editing, onEdit, onToggle, editor, adding, onAdd, add, connecting, onConnect, vertical }: {
  dests: Destination[]; now: number; editing?: number; onEdit: (id: number) => void; onToggle: (d: Destination) => void; editor: ReactNode
  /** Add output (owner, 19.10): the catalog and the form open under the header, as in the Studio */
  adding: boolean; onAdd: () => void; add: ReactNode
  connecting?: string | null; onConnect: (p: 'youtube' | 'twitch') => void
  /** VERT-1: the vertical input is on — an "Input" column says what each output sends */
  vertical?: boolean
}) {
  const max = useMaxOutputs()
  const full = dests.length >= max
  // the system's MetricsTable, compact rows (Status): label heads, numbers right in mono, a
  // sparkline before the bitrate, "—" for no data (AUDIT ST-07)
  return (
    <section data-testid="status-outputs" aria-label={t('status.outputs')} className="sx-panel !p-0">
      <div className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-3 py-1.5">
        <span className="sx-label">{t('status.outputs')}</span>
        <span data-testid="status-outputs-limit" className="text-[11px] text-muted">{full ? t('outs.full', { max }) : t('outs.limit', { n: dests.length, max })}</span>
        <Button size="sm" variant={adding ? 'default' : 'primary'} data-testid="status-add" aria-expanded={adding} disabled={full && !adding} onClick={onAdd}
          className="ml-auto max-md:min-h-[44px]">
          <Icon name={adding ? 'close' : 'plus'} size={16} />{adding ? t('common.close') : t('outs.add')}
        </Button>
      </div>
      {adding && <div data-testid="status-add-panel" className="border-b border-line px-4 py-3"><div className="max-w-[560px] animate-condense">{add}</div></div>}
      {dests.length === 0 ? <div className="sx-empty m-3"><Icon name="outputs" size={24} /><p className="m-0">{t('outs.empty')}</p></div> : (
        <div className="overflow-x-auto">
          <table data-testid="status-table" className="sx-table compact min-w-[640px]">
            <thead>
              <tr>
                <th scope="col">{t('status.col.output')}</th>
                <th scope="col">{t('status.col.state')}</th>
                {vertical && <th scope="col">{t('vert.sum.input')}</th>}
                <th scope="col" className="num">{t('status.col.bitrate')}</th>
                <th scope="col" className="num">{t('status.col.delay')}</th>
                <th scope="col" className="num">{t('status.col.watching')}</th>
                <th scope="col" className="num">{t('status.col.reconnects')}</th>
                <th scope="col"><span className="sr-only">{t('status.col.edit')}</span></th>
              </tr>
            </thead>
            <tbody>
              {dests.map((d) => {
                const b = badgeOf(d, now)
                const delay = d.delay?.totalMs
                return (
                  <Fragment key={d.id}>
                  <tr data-testid={`status-row-${d.id}`} data-state={d.apiState} className={editing === d.id ? 'sel' : undefined}>
                    <td className="!h-auto py-1.5"><span className="nm min-w-0"><LogoSlot platform={d.platform} dim={!d.enabled} /><span className="truncate">{d.name}</span><ProtocolChip protocol={d.protocol} platformLabel={d.profileName} testId={`status-protocol-${d.id}`} /></span>
                      <PlatformLine d={d} connecting={!!connecting && d.platformBlock?.connect === connecting} onConnect={onConnect} /></td>
                    <td><span className="flex items-center gap-2">
                      <RelayToggle on={d.enabled} busy={d.busy} testId={`status-toggle-${d.id}`}
                        label={`${d.name}: ${d.enabled ? 'turn off' : 'turn on'}`} onToggle={() => onToggle(d)} />
                      {/* room for the longest usual state ("CONNECTING", "RETRY 30 S"): the column keeps its width
                          while the state changes, nothing in the row moves (owner, 19.10) */}
                      <span className="inline-flex min-w-[calc(12ch+12*.08em+36px)]"><StatusBadge color={b.color} border={b.border}>{b.text}</StatusBadge></span></span></td>
                    {vertical && <td data-testid={`status-input-${d.id}`} className={d.vertical?.state === 'fallback' ? 'text-warn' : undefined}>{sourceWords(d)}</td>}
                    <td className="num tabular !h-auto py-1.5">
                      <span className="flex items-center justify-end gap-2"><Spark h={d.history} tone={rowTone(d)} /><span className="min-w-[4ch]">{d.bitrateMbps !== undefined ? d.bitrateMbps.toFixed(1) : <span className="text-muted">—</span>}</span></span>
                      {/* bitrate fill (features.fill): the mode and the filler share, here only (owner, 16.10) */}
                      {d.fill && <div className="mt-1 ml-auto w-[220px] text-left font-sans"><FillRow id={d.id} fill={d.fill} platform={d.platform} live={d.state === 'live'} /></div>}
                    </td>
                    <td className={`num tabular ${d.delay?.warning ? 'warn' : ''}`}>{delay !== undefined && d.state === 'live' ? `${d.delay?.kind === 'full' ? '' : '≈'}${delaySec(delay)}` : <span className="text-muted">—</span>}</td>
                    <td className="num tabular" data-testid={`status-watching-${d.id}`} title={platformTitle(d, now)}>
                      <span className="inline-flex items-center justify-end gap-1">{d.viewers !== undefined ? viewersText(d.viewers) : <span className="text-muted">—</span>}{d.platformBlock?.state === 'stale' && d.viewers !== undefined ? <Icon name="state-warn" size={12} className="text-warn" label={t('status.stale')} /> : null}</span>
                    </td>
                    <td className="num tabular" data-testid={`status-reconnects-${d.id}`}>{d.reconnects}</td>
                    <td className="text-right"><span className="inline-flex" data-testid={`status-edit-${d.id}`}><EditButton editing={editing === d.id} attention={d.needsKeyFix || d.needsKey} label={d.name} onClick={() => onEdit(d.id)} /></span></td>
                  </tr>
                  {editing === d.id && (
                    <tr data-testid={`status-editor-${d.id}`} className="sel">
                      <td colSpan={vertical ? 8 : 7} className="!h-auto whitespace-normal py-3"><div className="max-w-[560px] animate-condense">{editor}</div></td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/**
 * Live · Status (docs/live-studio.md §4): the whole stream at a glance — the summary,
 * the signal map, the outputs table, the input's link and (later) the events of this
 * broadcast. An output's settings open under its row, from the table or the map (owner, 18.10).
 * A separate address, /status: it can be bookmarked or kept open on a second screen.
 */
export function LiveStatus() {
  const L = useLiveScreen()
  const maxOutputs = useMaxOutputs()
  const { m, now } = L
  const desktop = useMedia('(min-width: 768px)')
  // the map needs the width of a laptop: below it the source panel and the table say the same (AUDIT ST-10)
  const wide = useMedia('(min-width: 1024px)')
  const loc = useLocation()
  const [obsOpen, setObsOpen] = useState(false)
  const closeObs = useCallback(() => setObsOpen(false), [])
  const lastLive = useRef<number | null>(null)
  const [editing, setEditing] = useState<number>()
  const [adding, setAdding] = useState<{ pick?: Profile } | null>(null)
  const [dual, setDual] = useState(false)
  // one panel at a time: settings of an output, or Add output
  const onEdit = useCallback((id: number) => {
    setAdding(null)
    setEditing((e) => (e === id ? undefined : id))
  }, [])
  const onAdd = useCallback(() => {
    setEditing(undefined)
    setAdding((a) => (a ? null : {}))
  }, [])
  // the map's edit button opens the row's settings below: bring them into view
  useEffect(() => {
    if (editing !== undefined) document.querySelector(`[data-testid="status-editor-${editing}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [editing])
  if (!L.view || !m) {
    return <p className="px-4 py-10 text-sm text-muted md:px-8" data-testid="live-wait" data-conn={L.conn}>{L.conn === 'limited' ? t('studio.limited') : L.conn === 'reconnecting' ? t('studio.reconnecting') : t('studio.loading')}</p>
  }
  if (m.summary.live) lastLive.current = now
  const dropped = isDropped(m, lastLive.current !== null && now - lastLive.current < 5 * 60_000)
  const need = m.dests.filter(needsYou).length
  const theme = new URLSearchParams(loc.search).get('theme')
  const studio = theme ? `/?theme=${theme}` : '/'
  const src = m.source
  const editingDest = editing !== undefined ? m.dests.find((d) => d.id === editing) : undefined
  const row = editingDest ? L.saved?.find((x) => x.id === editingDest.id) : undefined
  const close = () => setEditing(undefined)
  const editor = editingDest ? (
    L.profiles && row ? (
      <DestinationEditor key={editingDest.id} dest={editingDest} saved={row} profiles={L.profiles} streamLive={m.summary.live} fill={L.fill}
        onCancel={close}
        onSubmit={async (b) => { await L.update(editingDest.id, b); close() }}
        onRemove={async () => { await L.remove(editingDest.id); close() }} />
    ) : (
      <p className="text-xs text-muted">{t('studio.loadingSettings')}</p>
    )
  ) : null
  const toggle = (d: Destination) => void L.toggle(d.id, d.enabled, d.name)
  const add = adding ? (
    adding.pick ? (
      <AddForm key={adding.pick.id} profile={adding.pick} live={m.summary.live} count={m.dests.length} max={maxOutputs}
        onCancel={() => setAdding({})}
        onCreate={async (b) => { await L.create(b); setAdding(null) }} />
    ) : (
      <Catalog profiles={L.profiles} onPick={(p) => setAdding({ pick: p })} onDual={() => { setAdding(null); setDual(true) }} />
    )
  ) : null
  const rtmpCells: [string, string][] = [
    [t('strip.rtt'), src.delay && src.delay.rttMs > 0 ? `${Math.round(src.delay.rttMs)} ms` : '—'],
    [t('strip.jitter'), src.jitterMs !== undefined ? `${src.jitterMs} ms` : '—'],
    [t('status.reconnects'), String(src.reconnects)],
    [t('strip.bitrate'), src.uploadMbps !== undefined ? `${src.uploadMbps.toFixed(1)} Mbps` : '—'],
    [t('strip.keyframes'), m.summary.keyframeSec ? `${m.summary.keyframeSec.toFixed(1)} s` : '—'],
    [t('status.encoder'), src.sending ? src.app : '—'],
  ]

  return (
    <div data-testid="status-page" className="flex h-full min-h-0 flex-col">
      <StudioBar m={m} now={now} tab="status" dropped={dropped} phone={!desktop} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[1376px] flex-col gap-5 px-4 py-4 md:px-8 md:py-6">
          {L.connectBanner && (
            <div className="flex items-start gap-2" data-testid="connect-banner">
              <div className="flex-1"><Banner tone={L.connectBanner.tone}>{L.connectBanner.text}</Banner></div>
              <Button variant="ghost" icon onClick={L.closeConnectBanner} aria-label={t('common.dismiss')}><Icon name="close" size={16} /></Button>
            </div>
          )}
          <LiveSummary s={m.summary} now={now} needAttention={m.attention} onAir={m.onAir} onObs={() => setObsOpen(true)} />
          {need > 0 && (
            <p data-testid="status-fix" className="m-0 -mt-2 flex items-center gap-1.5 text-[13px] text-warn">
              <Icon name="state-warn" size={12} />{t('status.need', { n: need })} · <Link to={studio} className="inline-flex items-center gap-1 text-accent">{t('status.fix')}<Icon name="chevron-right" size={12} /></Link>
            </p>
          )}
          {(m.nodeDown || src.unknown) && (
            <Banner tone="warn"><span data-testid="node-unknown-banner">{t('studio.nodeDown', { node: src.node ? ` ${serverName(src.node, false)}` : '' })}</span></Banner>
          )}
          {L.err && <Banner tone="err">{L.err}</Banner>}

          {wide ? (
            <SignalMap source={src} dests={m.dests} now={now} viewers={m.summary.viewers} obsOpen={obsOpen} onObs={() => setObsOpen((v) => !v)} onObsClose={closeObs} editing={editing} onEdit={onEdit} onToggle={toggle} pair={m.vertical} />
          ) : (
            <SourceWidget source={src} now={now} editing={obsOpen} onEdit={() => setObsOpen((v) => !v)} />
          )}

          {/* charts: bitrate per output and the ingest, chart-1…4 (ScreenStatus, AUDIT ST-01, ST-06) */}
          <div className="grid items-stretch gap-4 lg:grid-cols-2">
            <section data-testid="status-bitrate" aria-label={t('status.chart.bitrate')} className="sx-panel flex flex-col gap-2">
              <span className="sx-label">{t('status.chart.bitrate')}</span>
              <BitrateChart series={m.dests.filter((d) => d.enabled).map((d) => ({ name: d.name, history: d.history }))} label={t('status.chart.bitrate')} />
            </section>
            <section data-testid="status-input" aria-label={t('status.input')} className="sx-panel flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="sx-label">{t('status.input')}{src.protocol ? ` · ${src.protocol.toUpperCase()}` : ''}</span>
                {src.node && <span className="ml-auto font-mono text-[11px] text-muted">{serverName(src.node)}</span>}
              </div>
              <BitrateChart series={[{ name: t('status.chart.ingest'), history: src.history }]} label={t('status.chart.ingestLabel')} height={160} />
              {/* the map's SRT block has the test ids; this is its summary by protocol */}
              <dl className="m-0 grid grid-cols-3 gap-x-3 gap-y-2.5 font-mono text-[13px]">
                {(src.srt ? srtCells(src.srt).map((c) => [c.label, c.value] as [string, string]) : rtmpCells).map(([k, v]) => (
                  <div key={k} className="min-w-0"><dt className="font-sans text-[11px] text-muted">{k}</dt><dd className="tabular m-0 truncate">{src.sending ? v : '—'}</dd></div>
                ))}
              </dl>
            </section>
          </div>

          {m.vertical?.on && <InputsTable m={m} pair={m.vertical} />}
          <OutputsTable vertical={!!m.vertical?.on} dests={m.dests} now={now} editing={editing} onEdit={onEdit} onToggle={toggle} editor={editor} adding={!!adding} onAdd={onAdd} add={add} connecting={L.connecting} onConnect={(p) => void L.connect(p)} />
          {/* the events feed waits for its API (AUDIT ST-08, outside DS-1) */}
          <section data-testid="status-events" aria-label={t('status.events')} className="sx-panel flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-2"><span className="sx-label">{t('status.events')}</span><Soon /></span>
            <p className="m-0 text-[13px] text-text-soft">{t('status.eventsSoon')}</p>
          </section>
        </div>
      </div>
      {dual && <YouTubeDualWizard streamName={src.streamName} phone={!desktop} onClose={() => setDual(false)} />}
    </div>
  )
}
